// c:\Users\Seshu\Downloads\Genmech\Genmech\client\src\utils\offlineAdapter.js
/* eslint-disable no-useless-escape */
import { db, generateObjectId, logOfflineStockMovement } from "./offlineDb";
import { API_BASE_URL } from "../config/api";

const toHeadersObject = (headers = {}) => {
  const nextHeaders = new Headers();

  if (headers instanceof Headers) {
    headers.forEach((value, key) => nextHeaders.set(key, value));
    return nextHeaders;
  }

  Object.entries(headers || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      nextHeaders.set(key, String(value));
    }
  });

  return nextHeaders;
};

const fetchWithTimeout = async (url, options = {}, timeoutMs = 0) => {
  const controller = new AbortController();
  const parentSignal = options.signal;
  let timeoutId;
  let timedOut = false;

  const abort = () => controller.abort();
  const abortForTimeout = () => {
    timedOut = true;
    abort();
  };

  if (parentSignal) {
    if (parentSignal.aborted) {
      abort();
    } else {
      parentSignal.addEventListener("abort", abort, { once: true });
    }
  }

  if (Number.isFinite(timeoutMs) && timeoutMs > 0) {
    timeoutId = window.setTimeout(abortForTimeout, timeoutMs);
  }

  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (error) {
    if (timedOut && error?.name === "AbortError") {
      error.code = "ECONNABORTED";
    }
    throw error;
  } finally {
    if (timeoutId) window.clearTimeout(timeoutId);
    parentSignal?.removeEventListener("abort", abort);
  }
};

const serializeParams = (params, paramsSerializer) => {
  if (!params) return "";
  if (typeof paramsSerializer === "function") {
    return paramsSerializer(params);
  }
  if (paramsSerializer && typeof paramsSerializer.serialize === "function") {
    return paramsSerializer.serialize(params);
  }
  if (params instanceof URLSearchParams) {
    return params.toString();
  }
  if (typeof params === "string") {
    return params;
  }
  if (typeof params === "object") {
    const sp = new URLSearchParams();
    Object.entries(params).forEach(([key, val]) => {
      if (val !== undefined && val !== null) {
        if (Array.isArray(val)) {
          val.forEach((item) => {
            if (item !== undefined && item !== null) sp.append(key, String(item));
          });
        } else {
          sp.append(key, String(val));
        }
      }
    });
    return sp.toString();
  }
  return "";
};

const requestViaFetch = async (config) => {
  const method = String(config.method || "get").toUpperCase();
  const headers = toHeadersObject(config.headers);
  const hasBody = !["GET", "HEAD"].includes(method);
  let body = config.data;

  if (hasBody && body && typeof body === "object" && !(body instanceof FormData) && !(body instanceof Blob) && !(body instanceof ArrayBuffer)) {
    if (!headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    body = JSON.stringify(body);
  }

  let requestUrl = config.url;
  if (requestUrl && requestUrl.startsWith("/")) {
    requestUrl = `${API_BASE_URL}${requestUrl}`;
  }

  const queryParamsString = serializeParams(config.params, config.paramsSerializer);
  if (queryParamsString) {
    requestUrl += (requestUrl.includes("?") ? "&" : "?") + queryParamsString;
  }

  let response;
  try {
    response = await fetchWithTimeout(
      requestUrl,
      {
        method,
        headers,
        body: hasBody ? body : undefined,
        credentials: config.withCredentials ? "include" : "same-origin",
        cache: "no-store",
        signal: config.signal
      },
      Number(config.timeout) || 0
    );
  } catch (error) {
    if (error?.code === "ECONNABORTED") {
      const timeoutError = new Error(`Request timed out after ${config.timeout}ms`);
      timeoutError.code = "ECONNABORTED";
      timeoutError.config = config;
      throw timeoutError;
    }
    throw error;
  }

  const responseHeaders = {};
  response.headers.forEach((value, key) => {
    responseHeaders[key] = value;
  });

  const responseType = config.responseType || "json";
  let data;
  if (responseType === "arraybuffer") {
    data = await response.arrayBuffer();
  } else if (responseType === "blob") {
    data = await response.blob();
  } else if (responseType === "text") {
    data = await response.text();
  } else {
    const text = await response.text();
    if (!text) {
      data = null;
    } else {
      try {
        data = JSON.parse(text);
      } catch (error) {
        data = text;
      }
    }
  }

  const axiosResponse = {
    data,
    status: response.status,
    statusText: response.statusText,
    headers: responseHeaders,
    config,
    request: null
  };

  if (!response.ok) {
    const error = new Error(`Request failed with status code ${response.status}`);
    error.config = config;
    error.request = null;
    error.response = axiosResponse;
    throw error;
  }

  return axiosResponse;
};

const ONLINE_CACHE_TTL_MS = 20000; // Cache online status for 20 seconds
let cachedOnlineStatus = null;
let lastOnlineCheckTime = 0;
let pendingOnlinePromise = null;

if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    cachedOnlineStatus = true;
    lastOnlineCheckTime = Date.now();
  });
  window.addEventListener("offline", () => {
    cachedOnlineStatus = false;
    lastOnlineCheckTime = Date.now();
  });
}

/**
 * Checks if the system is truly online by testing navigator and a fast ping check.
 * Cached for 20 seconds and deduplicates concurrent in-flight checks.
 */
export const checkRealOnlineStatus = async (forceRefresh = false) => {
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    cachedOnlineStatus = false;
    lastOnlineCheckTime = Date.now();
    return false;
  }

  const now = Date.now();
  if (!forceRefresh && cachedOnlineStatus !== null && now - lastOnlineCheckTime < ONLINE_CACHE_TTL_MS) {
    return cachedOnlineStatus;
  }

  if (pendingOnlinePromise) {
    return pendingOnlinePromise;
  }

  pendingOnlinePromise = (async () => {
    try {
      // Perform a fast lightweight fetch to our health endpoint
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/api/health`,
        { method: "HEAD", cache: "no-store" },
        4000
      ).catch(() => null);
      if (!response) {
        // Try base endpoint if API is not directly routed
        const baseResponse = await fetchWithTimeout(
          `${API_BASE_URL}/health`,
          { method: "HEAD", cache: "no-store" },
          4000
        ).catch(() => null);
        const isUp = !!baseResponse;
        cachedOnlineStatus = isUp;
        lastOnlineCheckTime = Date.now();
        return isUp;
      }
      cachedOnlineStatus = true;
      lastOnlineCheckTime = Date.now();
      return true;
    } catch (e) {
      cachedOnlineStatus = false;
      lastOnlineCheckTime = Date.now();
      return false;
    } finally {
      pendingOnlinePromise = null;
    }
  })();

  return pendingOnlinePromise;
};

const parseUrl = (urlStr) => {
  if (!urlStr) return null;
  let url = urlStr.replace(/^https?:\/\/[^\/]+/, "");
  url = url.split("?")[0]; // Remove query params

  const mappings = [
    { pattern: /^\/api\/customers$/, table: db.customers, entity: "Customer" },
    { pattern: /^\/api\/customers\/([^\/]+)$/, table: db.customers, entity: "Customer", hasId: true },
    { pattern: /^\/api\/dealers$/, table: db.dealers, entity: "Dealer" },
    { pattern: /^\/api\/dealers\/([^\/]+)$/, table: db.dealers, entity: "Dealer", hasId: true },
    { pattern: /^\/api\/customer-groups$/, table: db.customerGroups, entity: "CustomerGroup" },
    { pattern: /^\/api\/customer-groups\/([^\/]+)$/, table: db.customerGroups, entity: "CustomerGroup", hasId: true },
    { pattern: /^\/api\/project-expenses$/, table: db.projectExpenses, entity: "ProjectExpense" },
    { pattern: /^\/api\/project-expenses\/([^\/]+)$/, table: db.projectExpenses, entity: "ProjectExpense", hasId: true },
    { pattern: /^\/api\/vouchers$/, table: db.vouchers, entity: "Voucher" },
    { pattern: /^\/api\/vouchers\/([^\/]+)$/, table: db.vouchers, entity: "Voucher", hasId: true },
    { pattern: /^\/api\/vouchers\/allowances$/, table: db.vouchers, entity: "VoucherAllowance" },
    { pattern: /^\/api\/travel$/, table: db.travels, entity: "Travel" },
    { pattern: /^\/api\/travel\/([^\/]+)$/, table: db.travels, entity: "Travel", hasId: true },
    { pattern: /^\/api\/complaints$/, table: db.complaints, entity: "Complaint" },
    { pattern: /^\/api\/complaints\/([^\/]+)$/, table: db.complaints, entity: "Complaint", hasId: true },
    { pattern: /^\/api\/visits$/, table: db.visits, entity: "Visit" },
    { pattern: /^\/api\/visits\/([^\/]+)$/, table: db.visits, entity: "Visit", hasId: true },
    { pattern: /^\/api\/daily-updates$/, table: db.dailyUpdates, entity: "DailyUpdate" },
    { pattern: /^\/api\/daily-updates\/([^\/]+)$/, table: db.dailyUpdates, entity: "DailyUpdate", hasId: true },
    { pattern: /^\/api\/accounting-notes$/, table: db.accountingNotes, entity: "AccountingNote" },
    { pattern: /^\/api\/accounting-notes\/([^\/]+)$/, table: db.accountingNotes, entity: "AccountingNote", hasId: true },
    { pattern: /^\/api\/general-services$/, table: db.generalServices, entity: "GeneralService" },
    { pattern: /^\/api\/general-services\/([^\/]+)$/, table: db.generalServices, entity: "GeneralService", hasId: true },
    { pattern: /^\/api\/delivery-challans$/, table: db.deliveryChallans, entity: "DeliveryChallan" },
    { pattern: /^\/api\/delivery-challans\/([^\/]+)$/, table: db.deliveryChallans, entity: "DeliveryChallan", hasId: true },
    { pattern: /^\/api\/earth-pits$/, table: db.earthPits, entity: "EarthPit" },
    { pattern: /^\/api\/earth-pits\/([^\/]+)$/, table: db.earthPits, entity: "EarthPit", hasId: true },
    { pattern: /^\/api\/material-stock\/storage-locations$/, table: db.storageLocations, entity: "StorageLocation" },
    { pattern: /^\/api\/material-stock\/storage-locations\/([^\/]+)$/, table: db.storageLocations, entity: "StorageLocation", hasId: true },
    { pattern: /^\/api\/material-stock\/storage-areas$/, table: db.storageAreas, entity: "StorageArea" },
    { pattern: /^\/api\/material-stock\/storage-areas\/([^\/]+)$/, table: db.storageAreas, entity: "StorageArea", hasId: true },
    { pattern: /^\/api\/material-stock\/inventory$/, table: db.inventories, entity: "MaterialInventory" },
    { pattern: /^\/api\/material-stock$/, table: db.materialStocks, entity: "MaterialStock" },
    { pattern: /^\/api\/material-stock\/([^\/]+)$/, table: db.materialStocks, entity: "MaterialStock", hasId: true },
    { pattern: /^\/api\/quotations$/, table: db.quotations, entity: "Quotation" },
    { pattern: /^\/api\/quotations\/([^\/]+)$/, table: db.quotations, entity: "Quotation", hasId: true }
  ];

  for (const m of mappings) {
    const match = url.match(m.pattern);
    if (match) {
      return {
        table: m.table,
        entity: m.entity,
        id: m.hasId ? match[1] : null
      };
    }
  }
  return null;
};

// Custom Axios Adapter
export const offlineAdapter = async (config) => {
  const parsed = parseUrl(config.url);
  
  // If the endpoint is not part of our offline data models, pass directly to default network adapter
  if (!parsed) {
    return requestViaFetch(config);
  }

  const { table, id } = parsed;
  const method = config.method.toUpperCase();

  // Check connection status independently from queue depth.
  // Pending offline writes should not stop us from fetching fresh server data.
  const isOnline = await checkRealOnlineStatus();

  if (isOnline) {
    try {
      // 1. Run request online
      const response = await requestViaFetch(config);
      cachedOnlineStatus = true;
      lastOnlineCheckTime = Date.now();
      
      // 2. Synchronize local IndexedDB cache with server's confirmed return values in background
      if (method === "GET") {
        const serverData = response.data;
        if (Array.isArray(serverData) && serverData.length > 0) {
          void (async () => {
            try {
              const syncedItems = serverData.filter(Boolean).map((item) => ({
                ...item,
                syncStatus: "synced",
                lastSyncedAt: new Date().toISOString()
              }));
              await table.bulkPut(syncedItems);
            } catch (e) {
              console.warn("Offline cache sync error:", e);
            }
          })();
        } else if (serverData && serverData._id) {
          void table.put({ ...serverData, syncStatus: "synced", lastSyncedAt: new Date().toISOString() }).catch(() => {});
        }
      } else if (method === "POST" || method === "PUT") {
        const savedRecord = response.data;
        if (savedRecord && savedRecord._id) {
          void table.put({ ...savedRecord, syncStatus: "synced", lastSyncedAt: new Date().toISOString() }).catch(() => {});
        }
      } else if (method === "DELETE" && id) {
        void table.delete(id).catch(() => {});
      }

      return response;
    } catch (error) {
      // Fallback to offline mode on connection dropout or timeout
      const isNetworkError = !error.response || error.code === "ERR_NETWORK" || error.code === "ECONNABORTED";
      if (!isNetworkError) {
        throw error; // Return validation or auth errors normally
      }
      cachedOnlineStatus = false;
      lastOnlineCheckTime = Date.now();
      console.warn("Network request failed, falling back to offline database:", error);
    }
  }

  // --- OFFLINE/FALLBACK OPERATION ---
  console.log(`[Offline Mode] Intercepted ${method} ${config.url}`);

  if (method === "GET") {
    if (id) {
      const record = await table.get(id);
      if (!record || record.isDeleted) {
        return Promise.reject({
          response: { status: 404, statusText: "Not Found", data: { message: "Item not found offline" } }
        });
      }
      return { data: record, status: 200, statusText: "OK", headers: {}, config };
    } else {
      // Fetch all non-deleted records
      let records = await table.toArray();
      records = records.filter((r) => !r.isDeleted);

      // Filter by entity type if we are sharing the table (vouchers/allowances)
      if (parsed && parsed.entity === "Voucher") {
        records = records.filter((r) => r.expenseType !== undefined || r.voucherDate !== undefined);
      } else if (parsed && parsed.entity === "VoucherAllowance") {
        records = records.filter((r) => r.givenByRole !== undefined || r.allowanceDate !== undefined);
      }

      // Basic offline query filtering if requested
      let urlObj;
      try {
        urlObj = new URL(config.url, "http://localhost");
      } catch (e) {
        urlObj = { searchParams: new URLSearchParams() };
      }
      const searchParams = urlObj.searchParams;
      const getParam = (key) => {
        if (config.params && config.params[key] !== undefined && config.params[key] !== null) {
          return String(config.params[key]);
        }
        return searchParams.get(key);
      };

      const searchQuery = getParam("search");
      if (searchQuery) {
        const term = searchQuery.toLowerCase();
        records = records.filter((r) => {
          return Object.values(r).some((v) => String(v).toLowerCase().includes(term));
        });
      }

      const customerIdParam = getParam("customerId");
      if (customerIdParam) {
        const cTerm = customerIdParam.toLowerCase();
        records = records.filter((r) => {
          return (
            String(r.customerId || "").toLowerCase() === cTerm ||
            String(r._id || "").toLowerCase() === cTerm
          );
        });
      }

      return { data: records, status: 200, statusText: "OK", headers: {}, config };
    }
  }

  if (method === "POST") {
    const payload = JSON.parse(config.data || "{}");
    const newId = payload._id || generateObjectId();
    const offlineRecord = {
      ...payload,
      _id: newId,
      localId: newId,
      syncStatus: "pending_create",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    // Save record to local database
    await table.put(offlineRecord);

    // If this is a stock transaction, deduct stock values locally
    if (parsed.entity === "MaterialStock") {
      await logOfflineStockMovement(offlineRecord);
    }

    // Append operation to the persistent sync queue
    await db.syncQueue.add({
      url: config.url,
      method: "POST",
      data: JSON.stringify(offlineRecord),
      localId: newId,
      timestamp: new Date().toISOString()
    });

    return { data: offlineRecord, status: 201, statusText: "Created", headers: {}, config };
  }

  if (method === "PUT" && id) {
    const payload = JSON.parse(config.data || "{}");
    const existing = await table.get(id);
    if (!existing) {
      return Promise.reject({
        response: { status: 404, statusText: "Not Found", data: { message: "Record not found to update" } }
      });
    }

    const updatedRecord = {
      ...existing,
      ...payload,
      syncStatus: existing.syncStatus === "pending_create" ? "pending_create" : "pending_update",
      updatedAt: new Date().toISOString()
    };

    await table.put(updatedRecord);

    await db.syncQueue.add({
      url: config.url,
      method: "PUT",
      data: JSON.stringify(updatedRecord),
      localId: id,
      timestamp: new Date().toISOString()
    });

    return { data: updatedRecord, status: 200, statusText: "OK", headers: {}, config };
  }

  if (method === "DELETE" && id) {
    const existing = await table.get(id);
    if (existing) {
      if (existing.syncStatus === "pending_create") {
        // Unsynchronized item deleted: simply wipe from DB and remove any sync tasks
        await table.delete(id);
        await db.syncQueue.where("localId").equals(id).delete();
      } else {
        // Soft delete: mark as deleted and queue sync deletion
        await table.update(id, {
          isDeleted: true,
          syncStatus: "pending_delete"
        });

        await db.syncQueue.add({
          url: config.url,
          method: "DELETE",
          localId: id,
          timestamp: new Date().toISOString()
        });
      }
    }

    return { data: { success: true }, status: 200, statusText: "OK", headers: {}, config };
  }

  return Promise.reject({
    response: { status: 405, statusText: "Method Not Allowed", data: { message: "Unsupported operation offline" } }
  });
};
